(ns hai.home.tips-and-tricks
  "The Tips and tricks card's shared insides, worn by the desktop and mobile
   cards alike: the \"Humble setup\" checklist — a progress dial over the steps
   for getting Humble onto every surface, each stamped done in its own box —
   and the card's ⋮ actions. Step state and persistence live in
   [[hai.home.onboarding]]; each card supplies its own frame."
  (:require
   [hai.home.onboarding :as home-onboarding]
   [hai.tasks.ui :as tasks-ui]
   [hai.ui.components :as c]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [<s >e]]
   [hai.ui.tokens :as t]
   [hai.web-app.auth :as auth]
   [lambdaisland.ornament :as o]
   [url.install :as install]))

(def ^:private help-icon (icons/mui-icon :outlined :help_outline))
(def ^:private done-all-icon (icons/mui-icon :outlined :done_all))

(def ^:private homepage-label-id "home-setup-homepage-label")

(o/defstyled checklist :div
  ;; The checklist's own heading: dial and title stack.
  [:.setup-header
   {:display "flex" :align-items "center" :gap "12px"
    :padding-bottom "10px"}]
  ;; The conic fill comes from an inline style — it moves with progress.
  [:.setup-dial
   {:width "32px" :height "32px" :border-radius "50%"
    :flex "0 0 auto"
    :display "flex" :align-items "center" :justify-content "center"
    :transition "background 0.4s ease"}
   [:.dial-count
    {:width "22px" :height "22px" :border-radius "50%"
     :background t/--card-surface
     :display "flex" :align-items "center" :justify-content "center"
     :font-size "11px" :font-weight "700"
     :color t/--card-forest
     :font-variant-numeric "tabular-nums"}]]
  [:.setup-headings
   {:display "flex" :flex-direction "column" :gap "1px"}]
  [:.setup-title
   {:font-size "12px" :font-weight "700"
    :letter-spacing "0.1em" :text-transform "uppercase"
    :color t/--note-kraft
    :margin "0"}]
  [:.setup-subline
   {:font-size "12px" :color t/--link-muted}]

  ;; Step rows
  [:.setup-step
   {:display "flex" :align-items "center" :gap "12px"
    :padding "6px 0"}]
  ;; The shared stampbox is sized for a task; a setup row's is a touch smaller.
  [tasks-ui/stampbox
   {:width "24px" :height "24px" :border-radius "6px"}
   [tasks-ui/completion-stamp
    {:width "22px" :height "22px"}
    [:.check {:font-size "15px"}]]]
  [:.step-label
   {:font-size "15px" :font-weight "500"
    :color t/--card-forest
    :transition "color 0.2s ease"}]
  ;; A step whose label does something: a link out, or an in-app action.
  [#{:a.step-label :button.step-label}
   {:display "inline-flex" :align-items "center" :gap "5px"
    :text-decoration "underline" :text-underline-offset "3px"
    :text-decoration-color t/--card-forest-14}
   [:&:hover {:color t/--note-kraft}]
   [:.step-arrow {:font-size "12px"}]]
  [:button.step-label
   {:padding "0" :border "none" :background "transparent"
    :font-family "inherit" :text-align "left"
    :cursor "pointer"}]
  [:.setup-step.done
   [:.step-label
    {:color t/--link-muted
     :text-decoration "line-through"
     :text-decoration-color t/--card-forest-14}]]
  ;; Pushes the switch to the row's right edge.
  [:.step-spacer {:flex "1"}])

(defn menu-actions
  "The card's ⋮ actions, for [[hai.entity.kebab/menu]], over the card's
   `steps`."
  [steps]
  [["Mark all as done"
    #(>e [::home-onboarding/mark-all-done steps])
    done-all-icon
    (zero? @(<s [::home-onboarding/remaining steps]))]
   ;; Reading the guide is not progress — this never stamps anything.
   ["Setup help" install/setup-guide-url help-icon]])

(defn- subline-text
  [remaining]
  (case remaining
    0 "You’re all set"
    1 "One step left"
    (str remaining " steps left")))

(defn- dial
  [total remaining]
  (let [complete (- total remaining)
        percent  (/ (* 100 complete) total)]
    [:div.setup-dial
     {:style {:background (str "conic-gradient(" (o/rvalue t/--task-accent) " 0 " percent "%, "
                               (o/rvalue t/--card-forest-10) " " percent "% 100%)")}}
     [:div.dial-count (if (zero? remaining) "✔" (str remaining))]]))

(defn- setup-header
  [total remaining]
  [:div.setup-header
   [dial total remaining]
   [:div.setup-headings
    [:h3.setup-title "Humble setup"]
    [:span.setup-subline (subline-text remaining)]]])

;; A step's label is a link out (`:href`), an in-app action the host supplies
;; (`:on-click`), or — neither — the home-page step with its switch. Like the
;; link, the action never stamps the step.
(defn- step-row
  [{:keys [key label href on-click]} done?]
  [:div.setup-step {:class (when done? "done")}
   [tasks-ui/stampbox
    {:done?      done?
     :task-title label
     :title      (if done? "Mark not done" "Mark done")
     :on-toggle  #(>e [::home-onboarding/toggle-step key])}]
   (cond
     href
     [:a.step-label {:href href :target "_blank"}
      label
      [:span.step-arrow "↗"]]

     on-click
     [:button.step-label {:type "button" :on-click on-click}
      label
      [:span.step-arrow "→"]]

     :else
     [:<>
      [:span.step-label {:id homepage-label-id} label]
      [:div.step-spacer]
      ;; The switch drives the real setting; the stamp beside it only records
      ;; that the user is finished with the step. Skipping is allowed, so
      ;; neither one moves the other.
      ;;
      ;; Named by the visible step rather than a separate `aria-label`, so the
      ;; accessible name and the on-screen text can't diverge.
      [c/Switch
       {:checked           @(<s [::home-onboarding/homepage-override?])
        :aria-labelledby   homepage-label-id
        :on-checked-change #(>e [::auth/set-newtab-override %])}]])])

(defn setup-checklist
  "The \"Humble setup\" dial over `steps` (see [[hai.onboarding]])."
  [steps]
  (let [done      @(<s [::home-onboarding/done])
        remaining @(<s [::home-onboarding/remaining steps])]
    [checklist
     [setup-header (count steps) remaining]
     [:div.setup-list
      (for [{:keys [key] :as step} steps]
        ^{:key key} [step-row step (get done key)])]]))
